import { useState } from 'react';
import { Button, Input } from 'equasion-lib';
import { eqProps } from '../../eqProps';

type ReceiveManualEntryProps = {
  onSubmit: (unit: { serialNumber: string; gtin: string | null }) => void;
};

/**
 * Typed stand-in for a label that will not read, shown in SharedScanner's manual-entry modal.
 * An 18-digit SSCC is sent the way the camera reports one: the first 17 digits, which is what
 * the server concatenates `gtin + serial` against.
 */
function ReceiveManualEntry({ onSubmit }: ReceiveManualEntryProps) {
  const [kind, setKind] = useState<'unit' | 'sscc'>('unit');
  const [gtin, setGtin] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [sscc, setSscc] = useState('');
  const [sent, setSent] = useState<string | null>(null);

  const unitReady = !!gtin.trim() && !!serialNumber.trim();
  const ssccDigits = sscc.replace(/\D/g, '');
  const ssccReady = ssccDigits.length === 17 || ssccDigits.length === 18;

  const submitUnit = () => {
    if (!unitReady) return;
    const serial = serialNumber.trim();
    onSubmit({ serialNumber: serial, gtin: gtin.trim() });
    setSerialNumber('');
    setSent(`Serial ${serial} sent.`);
  };

  const submitSscc = () => {
    if (!ssccReady) return;
    const serial = ssccDigits.length === 18 ? ssccDigits.slice(0, 17) : ssccDigits;
    onSubmit({ serialNumber: serial, gtin: null });
    setSscc('');
    setSent('SSCC sent.');
  };

  return (
    <div className="receive-manual">
      <strong>Enter a label that will not scan</strong>
      <div className="receive-manual__kinds" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'unit'}
          className={kind === 'unit' ? 'active' : ''}
          onClick={() => setKind('unit')}
        >
          GTIN + serial
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'sscc'}
          className={kind === 'sscc' ? 'active' : ''}
          onClick={() => setKind('sscc')}
        >
          SSCC
        </button>
      </div>
      {kind === 'unit' ? (
        <>
          <Input
            {...eqProps({
              form: false,
              label: 'GTIN',
              value: gtin,
              onChange: ({ value }: { value: string }) => setGtin(value)
            })}
          />
          <Input
            {...eqProps({
              form: false,
              label: 'Serial Number',
              value: serialNumber,
              onChange: ({ value }: { value: string }) => setSerialNumber(value)
            })}
          />
          <Button type="primary" fullWidth disabled={!unitReady} {...eqProps({ onClick: submitUnit })}>
            Check product
          </Button>
        </>
      ) : (
        <>
          <Input
            {...eqProps({
              form: false,
              label: 'SSCC',
              value: sscc,
              onChange: ({ value }: { value: string }) => setSscc(value)
            })}
          />
          <Button type="primary" fullWidth disabled={!ssccReady} {...eqProps({ onClick: submitSscc })}>
            Check pallet
          </Button>
        </>
      )}
      {sent && (
        <p className="receive-manual__sent" role="status">
          {sent} Watch the list for the result.
        </p>
      )}
    </div>
  );
}

export default ReceiveManualEntry;
